import { useEffect, useRef, useState } from 'react'
import { useStreakQuery } from '@/api/hooks'
import { GoldIcon, WoodIcon, PrestigeIcon, LifebuoyIcon, LanternIcon, CheckIcon } from '@/components/game/GameIcons'
import { frenchOrdinal } from './StreakCard'

/**
 * Chip « lanterne » du header — le pendant compact de la carte au trésor.
 * ALLUMÉE (or + halo) quand la croix du jour est tracée, ÉTEINTE (gris-bleu,
 * sans halo) sinon. Tap/clic → petit panneau ancré : compteur, croix du jour,
 * trésor du prochain palier (récompenses en icônes) et jours d'escale en réserve.
 *
 * Densité mobile : lanterne seule + badge nombre en dessous de `sm` ; le
 * compteur passe inline dès que la barre a de la place.
 */
export function StreakChip() {
  const { data } = useStreakQuery()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // Fermeture au clic extérieur + Escape (actifs seulement quand ouvert).
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (!data) return null

  const { count, todayDone, shoreLeaves, nextMilestone: m } = data
  const jours = `${count} jour${count > 1 ? 's' : ''}`

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Carte au trésor : ${jours}${todayDone ? ', croix du jour tracée' : ', croix du jour à tracer'}`}
        title="Carte au trésor"
        className={`relative flex items-center gap-1 h-9 px-1.5 rounded-md transition-colors hover:bg-ocean-700/40 ${open ? 'bg-ocean-700/40' : ''}`}
      >
        <span data-testid="streak-lantern" data-lit={todayDone} className="inline-flex">
          <LanternIcon
            className={
              todayDone
                ? 'w-5 h-5 text-sand-300 drop-shadow-[0_0_6px_rgba(229,204,143,0.7)]'
                : 'w-5 h-5 text-ocean-300/70'
            }
          />
        </span>
        {/* compteur inline quand la barre respire — masqué sur toute la tranche
            xl→3xl, la seule où la nav 8 entrées + la pilule ressources remplissent
            déjà la barre (cf. le débord nav/ressources corrigé en v0.74.1). La
            borne était à 2xl et elle était trop basse : à 1536 px le conteneur
            plafonne 64 px sous ce que la barre complète demande. */}
        <span className={`hidden sm:inline xl:hidden 3xl:inline text-sm tabular-nums ${todayDone ? 'text-sand-200' : 'text-ocean-300'}`}>
          {count}
        </span>
        {/* …badge compact sur mobile ET sur la tranche serrée xl→3xl — seulement
            quand une action est encore attendue (croix du jour à tracer) : une
            fois la croix tracée, la lanterne allumée suffit. */}
        {!todayDone && (
          <span
            data-testid="streak-count-badge"
            className="grid sm:hidden xl:grid 3xl:hidden absolute -bottom-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 place-items-center rounded-full bg-ocean-900 border border-sand-500/40 text-[10px] leading-none tabular-nums text-sand-200"
            aria-hidden="true"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop mobile uniquement : assombrit la page sous le header et ferme
              au tap — même structure que NotificationBell. Dimensionné en dvh
              explicite (pas inset-0) car le header flouté (backdrop-blur) est le
              bloc conteneur de nos enfants `fixed` — un inset-bottom se clipperait
              à la barre de 64px. Masqué dès `sm` (le desktop garde le popover). */}
          <div
            data-testid="streak-panel-backdrop"
            className="fixed inset-x-0 top-16 h-[calc(100dvh-4rem)] z-30 bg-ocean-900/70 sm:hidden"
            aria-hidden="true"
            onMouseDown={() => setOpen(false)}
          />
          {/* Feuille pleine largeur sur mobile (fixed, sous le header), popover
              ancré identique à l'existant à partir de `sm` (comme la cloche). */}
          <div
            role="dialog"
            aria-label="Carte au trésor"
            className="fixed left-2 right-2 top-[4.5rem] z-40 max-h-[calc(100dvh-5.5rem)] overflow-y-auto rounded-xl border border-sand-500/20 bg-ocean-900/95 backdrop-blur-md shadow-xl p-4 text-left sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-64 sm:max-h-none"
          >
          <p className="font-display text-sand-100">
            Carte au trésor : <span className="text-sand-300 tabular-nums">{jours}</span>
          </p>

          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-ocean-300">Croix du jour</dt>
              <dd className="text-ocean-100">
                {todayDone ? (
                  <span className="inline-flex items-center gap-1 text-sand-200">
                    <CheckIcon className="w-3.5 h-3.5 text-sand-300" /> tracée
                  </span>
                ) : (
                  <span>à tracer</span>
                )}
              </dd>
            </div>

            <div className="flex items-center justify-between gap-3">
              <dt className="text-ocean-300">Trésor du {frenchOrdinal(m.day)} jour</dt>
              <dd className="text-ocean-100 flex items-center gap-1.5 flex-wrap justify-end">
                {m.prestige > 0 && (
                  <span className="inline-flex items-center gap-0.5 tabular-nums text-sand-200">
                    +{m.prestige} <PrestigeIcon className="w-3 h-3 text-sand-300" />
                  </span>
                )}
                {m.gold > 0 && (
                  <span className="inline-flex items-center gap-0.5 tabular-nums text-sand-200">
                    +{m.gold.toLocaleString('fr-FR')} <GoldIcon className="w-3 h-3 text-sand-300" />
                  </span>
                )}
                {m.wood > 0 && (
                  <span className="inline-flex items-center gap-0.5 tabular-nums text-sand-200">
                    +{m.wood.toLocaleString('fr-FR')} <WoodIcon className="w-3 h-3 text-sand-300" />
                  </span>
                )}
              </dd>
            </div>
            {m.titleLabel && (
              <p className="text-xs text-sand-200 text-right -mt-1">
                titre <span className="font-display text-sand-300">« {m.titleLabel} »</span>
              </p>
            )}

            <div className="flex items-center justify-between gap-3">
              <dt className="text-ocean-300">Jours d'escale</dt>
              <dd className="inline-flex items-center gap-1 text-ocean-100">
                <LifebuoyIcon className="w-4 h-4 text-ocean-300" />
                <span className="tabular-nums">×{shoreLeaves}</span>
              </dd>
            </div>
          </dl>

          <p className="mt-3 text-xs italic text-ocean-300">
            {todayDone
              ? 'Croix du jour tracée. Reviens demain.'
              : "Trace ta croix du jour ; n'importe quelle action suffit."}
          </p>
          </div>
        </>
      )}
    </div>
  )
}
